import {
  type FileTypePreset,
  buildUnsupportedTypeMessage,
  partitionFilesByAllowed,
} from '@/shared/lib/fileTypes';
import { showErrorToast } from '@/shared/notifications/toast-utils';
import { Upload } from 'lucide-react';
import { useRef, useState } from 'react';

interface FileDropzoneProps {
  preset: FileTypePreset;
  multiple?: boolean;
  disabled?: boolean;
  onFilesAccepted: (files: File[]) => void;
  onFilesRejected?: (rejected: File[]) => void;
  /** Override the default body line. */
  label?: string;
  /** Override the default "Allowed: …" hint. */
  hint?: string;
}

export function FileDropzone({
  preset,
  multiple = true,
  disabled = false,
  onFilesAccepted,
  onFilesRejected,
  label = 'Drop files here or',
  hint,
}: FileDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFiles = (incoming: FileList | File[] | null | undefined) => {
    const { accepted, rejected } = partitionFilesByAllowed(incoming, preset);
    if (rejected.length > 0) {
      showErrorToast('File type not supported', buildUnsupportedTypeMessage(rejected, preset));
      onFilesRejected?.(rejected);
    }
    if (accepted.length > 0) {
      onFilesAccepted(accepted);
    }
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled) return;
    handleFiles(e.dataTransfer.files);
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (disabled) return;
    setIsDragging(true);
  };

  const onDragLeave = () => setIsDragging(false);

  const onBrowseClick = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
    e.target.value = '';
  };

  return (
    <div
      className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
        disabled
          ? 'border-border opacity-60 cursor-not-allowed'
          : isDragging
            ? 'border-primary bg-orange-50 dark:bg-orange-950/20'
            : 'border-border hover:border-primary hover:bg-muted/50 cursor-pointer'
      }`}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onClick={onBrowseClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onBrowseClick();
        }
      }}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
    >
      <div className="flex flex-col items-center gap-2">
        <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
          <Upload className="w-5 h-5 text-muted-foreground" />
        </div>
        <p className="text-sm text-foreground">
          {label} <span className="text-primary hover:underline">browse</span>
        </p>
        <p className="text-xs text-muted-foreground">{hint ?? `Allowed: ${preset.label}`}</p>
      </div>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        multiple={multiple}
        accept={preset.accept}
        disabled={disabled}
        onChange={onChange}
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}
